<template>
  <g ref="wrapper" :transform="transform" style="pointer-events: none">
    <path
      v-for="(segment, index) in segments"
      :key="index"
      :d="segment"
      class="tools path-tool line-edit"
      vector-effect="non-scaling-stroke"
      @pointerdown="onLinePointerDown($event, index)"
    />
    <g>
      <template v-if="currentNodeIndexes.size">
        <line
          v-for="(line, index) of indicatorLines"
          :key="index"
          :x1="line[0]"
          :y1="line[1]"
          :x2="line[2]"
          :y2="line[3]"
          class="tools path-tool indicator-line"
          vector-effect="non-scaling-stroke"
        />
      </template>
      <template v-for="([leftX, leftY, posX, posY, rightX, rightY], index) in pathData" :key="index">
        <template v-if="!isSame(posX, posY, rightX, rightY)">
          <line
            :x1="posX"
            :y1="posY"
            :x2="rightX"
            :y2="rightY"
            class="tools path-tool handle-line"
            vector-effect="non-scaling-stroke"
          />
          <circle
            :cx="rightX"
            :cy="rightY"
            :r="15 * factor"
            class="tools path-tool handle node-touch-area"
            vector-effect="non-scaling-stroke"
            @pointerdown.capture="onHandlePointerDown($event, index, 'right')"
          />
        </template>

        <Arrow
          v-else
          :pos="[posX, posY]"
          :cp1="[rightX, rightY]"
          :cp2="(pathData[index + 1] ?? pathData[0]).slice(0, 2)"
          vector-effect="non-scaling-stroke"
          :factor="factor"
          @pointerdown.capture="onArrowPointerDown($event, index, 'right')"
        />

        <template v-if="!isSame(posX, posY, leftX, leftY)">
          <line
            :x1="posX"
            :y1="posY"
            :x2="leftX"
            :y2="leftY"
            class="tools path-tool handle-line"
            vector-effect="non-scaling-stroke"
          />
          <circle
            :cx="leftX"
            :cy="leftY"
            :r="15 * factor"
            class="tools path-tool handle node-touch-area"
            vector-effect="non-scaling-stroke"
            @pointerdown.capture="onHandlePointerDown($event, index, 'left')"
          />
        </template>
        <Arrow
          v-else
          :pos="[posX, posY]"
          :cp1="[leftX, leftY]"
          :cp2="(pathData[index - 1] ?? pathData[pathData.length - 1]).slice(4)"
          vector-effect="non-scaling-stroke"
          :factor="factor"
          @pointerdown.capture="onArrowPointerDown($event, index, 'left')"
        />

        <rect
          ref="nodeRefs"
          :width="30 * factor"
          :height="30 * factor"
          :x="posX - 15 * factor"
          :y="posY - 15 * factor"
          class="tools path-tool node node-touch-area"
          vector-effect="non-scaling-stroke"
          @pointerdown="onNodePointerDown($event, index)"
        />
      </template>
    </g>
  </g>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import PathSection from '../models/sections/path';
import Arrow from './Arrow.vue';
import { getPointFromEvent, isSame, reflect, toPathToolPath } from './path-tool-utils';
import { usePathTool } from './usePathTool';
import Application from '../application';
import eventbus from 'tiny-emitter/instance';

const props = defineProps<{ section: PathSection; onKeyDown: (event: KeyboardEvent) => void }>();

// @ts-expect-error
const transform = props.section.view.transform('matrix') as string;
const pathData = reactive(toPathToolPath(props.section.shape.array().value as any));

const wrapper = ref<SVGGElement>();
const nodeRefs = ref<SVGRectElement[]>([]);

const currentNodeIndexes = reactive(new Set<number>());
const zoom = ref(Application.instance.zoomManager.zoom());

const updateZoom = (newZoom: number) => {
  zoom.value = Application.instance.zoomManager.zoom();
};

const factor = computed(() => 1 / (zoom.value * 4));

eventbus.on('camera:zoomed', updateZoom, this);

const { svg, indicatorLines, snappedMouse } = usePathTool(wrapper, pathData, zoom, currentNodeIndexes);

const pathString = computed(() => {
  if (!pathData.length) {
    return 'M0 0';
  }

  let d = pathData.reduce((acc: string, [leftX, leftY, posX, posY], index, arr) => {
    if (index === 0) {
      return 'M' + posX + ',' + posY;
    }

    const [lastRightX, lastRightY] = arr[index - 1].slice(-2);
    return `${acc} C${lastRightX},${lastRightY} ${leftX},${leftY} ${posX},${posY}`;
  }, '');

  const [lastRightX, lastRightY] = pathData[pathData.length - 1].slice(-2);
  const [leftX, leftY, posX, posY] = pathData[0];

  d += `C${lastRightX},${lastRightY} ${leftX},${leftY} ${posX},${posY}`;

  return d + 'Z';
});

const segments = computed(() => {
  return pathData.map(([_, __, startX, startY, c1x, c1y], index, arr) => {
    const [c2x, c2y, endX, endY] = arr[index + 1] ?? arr[0];

    return `M${startX} ${startY}C${c1x} ${c1y} ${c2x} ${c2y} ${endX} ${endY}`;
  });
});

// since we reuse onNodePointerDown for line movement,
// we have to control the indexes from the outside.
// Otherwise there will be a race condition when the first index is removed on pointerup.
const onNodePointerDown = (event: PointerEvent, index: number, ignoreIndexes = false) => {
  event.stopImmediatePropagation();

  if (!ignoreIndexes) {
    currentNodeIndexes.add(index);
  }
  let startCoords = [...snappedMouse.value];

  svg.value.setPointerCapture(event.pointerId);

  const onNodePointerMove = () => {
    const [clientX, clientY] = snappedMouse.value;

    const deltaX = clientX - startCoords[0];
    const deltaY = clientY - startCoords[1];
    startCoords = [clientX, clientY];

    const path = pathData[index] ?? pathData[0];

    path[0] += deltaX;
    path[1] += deltaY;
    path[2] += deltaX;
    path[3] += deltaY;
    path[4] += deltaX;
    path[5] += deltaY;
  };

  const onNodePointerUp = () => {
    onNodePointerMove();

    svg.value.releasePointerCapture(event.pointerId);
    svg.value.removeEventListener('pointermove', onNodePointerMove);
    svg.value.removeEventListener('pointerup', onNodePointerUp);

    if (!ignoreIndexes) {
      currentNodeIndexes.delete(index);
    }
  };

  svg.value.addEventListener('pointermove', onNodePointerMove);
  svg.value.addEventListener('pointerup', onNodePointerUp);
};

const onLinePointerDown = (event: PointerEvent, index: number) => {
  event.stopImmediatePropagation();

  const path = event.target as SVGPathElement;

  svg.value.classList.add('grabbing');
  path.classList.add('line-edit-moving');

  currentNodeIndexes.add(index);
  currentNodeIndexes.add((index + 1) % pathData.length);

  onNodePointerDown(event, index, true);
  onNodePointerDown(event, index + 1, true);

  const onLinePointerUp = () => {
    svg.value.removeEventListener('pointerup', onLinePointerUp);

    path.classList.remove('line-edit-moving');
    svg.value.classList.remove('grabbing');

    currentNodeIndexes.clear();
  };

  svg.value.addEventListener('pointerup', onLinePointerUp);
};

const setHandlePosition = (path: number[], pos: [number, number], event: PointerEvent, lr: 'left' | 'right') => {
  const isLeft = lr === 'left';
  const i1 = isLeft ? 0 : 4;
  const i2 = isLeft ? 4 : 0;

  // this sets the leftOrRight handle
  [path[i1], path[i1 + 1]] = pos;

  // this sets the opposit handle if shift is not pressed
  if (!event.shiftKey) {
    [path[i2], path[i2 + 1]] = reflect(pos, [path[2], path[3]]);
  }
};

const onArrowPointerDown = (event: PointerEvent, index: number, leftOrRight: 'left' | 'right') => {
  const { clientX, clientY } = getPointFromEvent(event, { node: wrapper.value! });

  const path = pathData[index];

  setHandlePosition(path, [clientX, clientY], event, leftOrRight);

  nextTick().then(() => {
    onHandlePointerDown(event, index, leftOrRight);
  });
};

const onHandlePointerDown = (event: PointerEvent, index: number, leftOrRight: 'left' | 'right') => {
  event.stopImmediatePropagation();
  const path = pathData[index];

  const onHandlePointerMove = (event: PointerEvent) => {
    const { clientX, clientY } = getPointFromEvent(event, { node: wrapper.value! });
    setHandlePosition(path, [clientX, clientY], event, leftOrRight);
  };

  const onHandlePointerUp = (event: PointerEvent) => {
    onHandlePointerMove(event);

    svg.value.removeEventListener('pointermove', onHandlePointerMove);
    svg.value.removeEventListener('pointerup', onHandlePointerUp);

    if (event.target === nodeRefs.value[index]) {
      // pointerup happened on the node the handle belongs to
      // so we remove the handles
      if (leftOrRight === 'left' || !event.shiftKey) {
        path[0] = path[2];
        path[1] = path[3];
      }

      if (leftOrRight === 'right' || !event.shiftKey) {
        path[4] = path[2];
        path[5] = path[3];
      }
    }
  };

  svg.value.addEventListener('pointermove', onHandlePointerMove);
  svg.value.addEventListener('pointerup', onHandlePointerUp);
};

const updateAttributes = () => {
  /* eslint-disable vue/no-mutating-props */
  nextTick(() => {
    props.section.attributes.path = pathString.value;
    props.section.shape.plot(pathString.value);
    const { cx, cy } = props.section.shape.bbox();
    props.section.attributes.position = [cx, cy];
  });
  /* eslint-enable  vue/no-mutating-props */
};

watch(pathString, updateAttributes);

onMounted(() => {
  window.addEventListener('keydown', props.onKeyDown);
});

onBeforeUnmount(() => {
  eventbus.off('camera:zoomed', updateZoom);
  window.removeEventListener('keydown', props.onKeyDown);
});
</script>
